/**
 * Main Application Component
 *
 * Root component that sets up:
 * - Material-UI theme
 * - React Router
 * - Centralized routing
 * - App layout
 * - Suspense for lazy-loaded components
 */

import { Suspense } from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { theme } from '../theme/muiTheme';
import { routes } from './routes';
import AppLayout from './AppLayout';
import { NotificationProvider } from '../contexts/NotificationContext';
import { ImpersonateProvider } from '../auth/ImpersonateContext';

/**
 * Loading fallback component for lazy-loaded routes
 */
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: 2,
    }}
  >
    <CircularProgress size={48} />
    <Typography variant="body1" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

/**
 * Routes component - renders route configuration with suspense
 */
function AppRoutes() {
  const element = useRoutes(routes);
  return <Suspense fallback={<LoadingFallback />}>{element}</Suspense>;
}

/**
 * Main App component
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <BrowserRouter>
          <ImpersonateProvider>
            <AppLayout>
              <AppRoutes />
            </AppLayout>
          </ImpersonateProvider>
        </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
